import React, { useState, useLayoutEffect, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import HeaderButton from '../../../components/Buttons/HeaderButton';
import OrganizationForm from './components/OrganizationForm';
import { HeaderBackButton } from '@react-navigation/stack';
import CustomSnackbar from '../../../components/CustomSnackbar';
import { useIsFocused } from '@react-navigation/native';
import LinkStyleButton from '../../../components/Buttons/LinkStyleButton';
import { MainColors } from '../../../constants/Colors';
import FontSizes from '../../../constants/FontSizes';
import DeleteModal from '../../../components/Modals/DeleteModal';
import LoadingIndicator from '../../../components/ActivityIndicator';
import useFetch from '../../../utils/useFetch';
import InfoMessageBox from '../../../components/InfoMessageBox';

const EditOrganizationScreen = ({ route, navigation }) => {
    const { organizationId } = route.params;
    const [organizationName, setOrganizationName] = useState();
    const [currentOrganizationName, setCurrentOrganizationName] = useState();
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const isFocused = useIsFocused();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isModalDeleteButtonDisabled, setIsModalDeleteButtonDisabled] = useState(true);
    const [deleteInputValue, setDeleteInputValue] = useState();
    const { get, put, doDelete, isLoading } = useFetch();
    const url = `/api/organizations/${organizationId}`;
    const [hasOrganizationBeenDeleted, setHasOrganizationBeenDeleted] = useState();

    useEffect(() => {
        async function getOrganizationData() {
            const response = await get(url);
            setCurrentOrganizationName(response.organization_name);
            setOrganizationName(response.organization_name);
            setHasOrganizationBeenDeleted(response.is_deleted);
        }

        getOrganizationData();
    }, [isFocused]);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: 'Edit Organization',
            headerRight: () => (
                <HeaderButton
                    onPress={onUpdateOrganizationPressedHandler}
                    accessibilityLabel="Save changes for organization"
                    accessibilityHint="Press this button to save changes for this organization"
                    buttonText="Save"
                    disabled={saveButtonDisabled}
                />
            ),
            headerLeft: () => (
                <HeaderBackButton
                    onPress={() => { navigation.goBack() }}
                    label="Back"
                    tintColor="#fff"
                />
            )
        });
    }, [navigation, saveButtonDisabled, snackbarVisible]);

    const onOrganizationNameChangedHandler = (organizationName) => {
        setOrganizationName(organizationName);

        if (organizationName && organizationName !== currentOrganizationName) {
            setSaveButtonDisabled(false);
        } else {
            setSaveButtonDisabled(true);
        }
    }

    const onUpdateOrganizationPressedHandler = async () => {
        await put(url, { organizationName: organizationName });
        setSaveButtonDisabled(true);
        setSnackbarMessage('Organization changes were saved.');
        setSnackbarVisible(true);
    }

    const onDeleteInputTextChangedHandler = (deleteInputValue) => {
        setDeleteInputValue(deleteInputValue);
        if (deleteInputValue === 'DELETE') {
            setIsModalDeleteButtonDisabled(false);
        } else {
            setIsModalDeleteButtonDisabled(true);
        }
    }

    const onDeleteOrganizationPressedHandler = async () => {
        try {
            await setIsDeleteModalVisible(false);
            await doDelete(url);
            setSaveButtonDisabled(true);
            setSnackbarMessage('Organization was successfully deleted.');
            setSnackbarVisible(true);
        } catch (error) {
            console.log(error);
        }
    }

    const onSnackbarDismissedHandler = () => {
        setSnackbarVisible(false);
        navigation.navigate('SuperDuperAdminOrganizationsDashboard');
    }

    return (
        <View style={styles.screen}>
            <LoadingIndicator isLoading={isLoading}>
                <View style={styles.container}>
                    <OrganizationForm
                        organizationNameValue={organizationName}
                        onChangeOrganizationNameText={onOrganizationNameChangedHandler}
                        formButtonText='Save Changes'
                        formButtonAccessibilityLabel='Save Changes'
                        formButtonAccessibilityHint='Press this button to save changes for this organization'
                        formButtonDisabled={saveButtonDisabled}
                        formButtonPressed={onUpdateOrganizationPressedHandler}
                        isNameEditable={hasOrganizationBeenDeleted ? false : true}
                        multiline={true}
                    />

                    {hasOrganizationBeenDeleted ? (
                        <InfoMessageBox style={{ marginVertical: 30 }} messageText='This organization has been deleted and cannot be edited.' />
                    ) : (
                            <LinkStyleButton
                                style={styles.deleteText}
                                buttonText='Delete Organization'
                                accessibilityLabel='Delete Organization'
                                accessibilityHint='Press this button to delete this organization'
                                onPress={() => setIsDeleteModalVisible(true)}
                            />
                        )}

                </View>
                <CustomSnackbar
                    message={snackbarMessage}
                    visible={snackbarVisible}
                    onDismiss={onSnackbarDismissedHandler}
                    actionLabel='GO BACK'
                    actionOnPress={() => navigation.navigate('SuperDuperAdminOrganizationsDashboard')}
                />
                <DeleteModal
                    visible={isDeleteModalVisible}
                    deleteButtonDisabled={isModalDeleteButtonDisabled}
                    onModalBackgroundPressed={() => setIsDeleteModalVisible(false)}
                    onCancelPressed={() => setIsDeleteModalVisible(false)}
                    itemToDelete={currentOrganizationName}
                    deleteInputValue={deleteInputValue}
                    onChangeDeleteInputText={onDeleteInputTextChangedHandler}
                    onModalDeletePress={onDeleteOrganizationPressedHandler}
                />
            </LoadingIndicator>
        </View>
    )
}

const styles = StyleSheet.create({
    screen: {
        flexGrow: 1
    },
    container: {
        paddingVertical: 30,
        paddingHorizontal: 20,
    },
    deleteText: {
        marginTop: 20,
        color: MainColors.danger,
        fontWeight: '500',
        fontSize: FontSizes.medium
    }
});

export default EditOrganizationScreen;